import * as React from 'react';

import * as ramda from 'ramda';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';

import {serializeFilters} from 'nutshell-core';

import {api} from 'nutshell-core/api';
import {getExportLinkFromResult} from 'nutshell-core/json-to-report-data';
import {Routing} from 'nutshell-core/routing';
import type {FilterObject, ReportsResponse} from 'nutshell-core/types';
import {getDisplayName} from 'nutshell-core/utils';

import {colors} from 'shells/colors/colors';
import * as ReportColors from 'shells/colors/reports';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/switchMap';

import type {ChartReportParams, DateRangeType, ODRReportData} from '../report-types';

import {ErrorChart} from './error-chart';
import {LoadingChart} from './loading-chart';

const REST_URL = '/leads/report';
const DEFAULT_BAR_COLOR = ReportColors.BASE_BAR_COLOR;

type HocProps = {
    dateRangeType: DateRangeType;
    isTimeseries: boolean;
    useMean?: boolean;
    cachedData?: ODRReportData;
    reportParams: ChartReportParams;
    filters: Array<FilterObject>;
    isOffline?: boolean;
    onReportDataFetched?: (...args: any) => any;
    onError?: (...args: any) => any;
};

type HocState =
    | {
          reportData: ODRReportData;
          isLoading: false;
          isRefreshing: boolean;
          isErrored: boolean;
          errorIsClosing: boolean;
      }
    | {
          isLoading: true;
          isRefreshing: boolean;
          isErrored: boolean;
          errorIsClosing: boolean;
      };

export function addChartDataFetching(WrappedChartComponent: React.ComponentType<any>) {
    class Hoc extends React.Component<HocProps, HocState> {
        static displayName = `HOC(${getDisplayName(WrappedChartComponent)})`;
        errorMessage = undefined;

        requestStream: Subject<any>;

        static defaultProps = {
            isTimeseries: false,
        };

        constructor(props: HocProps) {
            super(props);
            this.state = {
                // @ts-expect-error
                reportData: undefined,
                isLoading: true,
                isRefreshing: false,
                isErrored: false,
                errorIsClosing: false,
            };

            this.requestStream = new Subject();

            // We only want to trigger updates from the latest request. So we
            // use switch map to ignore all any requests still in flight.
            const responseStream = this.requestStream
                .switchMap((request) => Observable.fromPromise(request))
                // @ts-expect-error
                .catch(this.handleFailedFetch);

            // @ts-expect-error
            responseStream.subscribe(notifyParent(this.props.onReportDataFetched));
            // @ts-expect-error
            responseStream.subscribe(this.saveFetchedData);
        }

        UNSAFE_componentWillMount() {
            this.checkForCachedData(this.props.cachedData);
        }

        componentDidMount() {
            if (typeof this.props.cachedData === 'undefined') {
                this.fetchData(this.props);
            }
        }

        UNSAFE_componentWillReceiveProps(nextProps: HocProps) {
            const {
                dateRangeType: nextDateRangeType,
                isTimeseries: nextIsTimeseries,
                cachedData: nextCachedData,
                reportParams: {
                    gap: nextGap,
                    facet: nextFacet,
                    segment: nextSegment,
                    useConfidence: nextUseConfidence,
                },
                filters: nextFilters,
            } = nextProps;

            const {
                dateRangeType,
                cachedData,
                reportParams: {gap, facet, segment, useConfidence},
                filters,
            } = this.props;

            if (!nextProps.isOffline && this.props.isOffline) {
                this.fetchData(this.props);
            }

            if (nextCachedData !== cachedData) {
                this.checkForCachedData(nextCachedData);
            }

            const isFaceted = nextFacet !== undefined;
            const isSegmented = nextSegment !== undefined;
            const isSegmentedButNotTimeseries = isSegmented && !nextIsTimeseries;
            const dateRangeTypeChanged = nextDateRangeType !== dateRangeType;
            const filtersChanged = !ramda.equals(nextFilters, filters);
            const gapChanged = nextGap !== gap;
            const facetChanged = nextFacet !== facet;
            const segmentChanged = nextSegment !== segment;
            const useConfidenceChanged = nextUseConfidence !== useConfidence;

            const timeseriesPropsChanged = gapChanged;
            const facetedPropsChanged = facetChanged;
            const universalPropsChanged = dateRangeTypeChanged || filtersChanged;

            if (
                (nextIsTimeseries && timeseriesPropsChanged) ||
                (isFaceted && !isSegmented && facetedPropsChanged && !nextCachedData) ||
                (isSegmented && segmentChanged) ||
                (isSegmentedButNotTimeseries && useConfidenceChanged) ||
                universalPropsChanged
            ) {
                this.fetchData(nextProps);
            }
        }

        render() {
            if (this.state.isErrored) {
                // Not keeping this in state, because we can't `setState` from render cycle
                this.errorMessage = getErrorMessage(this.errorMessage);

                return (
                    <ErrorChart
                        onRetry={this.handleErrorRetry}
                        showLoading={this.state.isLoading || this.state.errorIsClosing}
                        // @ts-expect-error
                        errorMessage={this.errorMessage}
                        vertical={this.props.isTimeseries}
                    />
                );
            }

            if (this.state.isLoading) {
                return <LoadingChart />;
            }

            const {
                // These props we don't want to pass to chart
                /* eslint-disable @typescript-eslint/no-unused-vars */
                cachedData,
                isOffline,
                onReportDataFetched,
                onError,

                reportParams: {gap, facet, groupByField, segment, useConfidence},
                filters,
                ...rest
            } = this.props;

            return (
                <WrappedChartComponent
                    {...rest}
                    reportGap={gap}
                    facet={facet}
                    filters={filters}
                    segment={segment}
                    groupByField={groupByField}
                    useConfidence={useConfidence}
                    data={this.state.reportData ? this.state.reportData.chartData : []}
                    isRefreshing={this.state.isRefreshing}
                />
            );
        }

        handleErrorRetry = (e: React.SyntheticEvent, closeBanner: (...args: any) => any) => {
            this.setState(closingError);

            this.fetchData(this.props).then(() => {
                closeBanner().then(() => {
                    this.setState(clearError);
                    this.setState(stopClosingError);
                    this.errorMessage = undefined;
                });
            });
        };

        handleFailedFetch = () => {
            // @ts-expect-error
            this.setState(failFetch);
            if (this.props.onError) {
                this.props.onError();
            }
        };

        checkForCachedData = (cachedData?: ODRReportData) => {
            if (cachedData) {
                // @ts-expect-error
                this.setState(applyCachedData(cachedData));
                this.setState(stopLoading);
            }
        };

        saveFetchedData = ({reportData}: {reportData: ODRReportData}) => {
            // @ts-expect-error
            this.setState(setData(reportData));
            this.setState(stopLoading);
            this.setState(clearError);
        };

        fetchData({dateRangeType, reportParams, filters}: HocProps): Promise<any> {
            this.setState(startLoading);
            const params = getPostParams(dateRangeType, reportParams, filters);
            const exportDataLink = `${REST_URL}?${Routing.param(
                getPostParams(dateRangeType, reportParams, filters, 'csv')
            )}`;

            const request = api
                .get(REST_URL, params)
                // @ts-expect-error
                .then((response) => response.json())
                .then(transformDataForReport)
                // @ts-expect-error
                .then(addExportLink(exportDataLink));

            this.requestStream.next(request);

            return request;
        }
    }

    return Hoc;
}

function getPostParams(
    // @ts-expect-error
    dateRangeType,
    // @ts-expect-error
    reportParams,
    filters: Array<FilterObject>,
    format?: string | null
) {
    const {gap, facet, segment, groupByField, reportType, useConfidence} = reportParams;
    const groupBy = groupByField || dateRangeType || segment;

    return {
        filter: serializeFilters(filters),
        groupByField: groupBy,
        pivotFacet: facet,
        gap,
        reportType,
        useConfidence,
        segmentByField: segment,
        format,
    };
}

// @ts-expect-error
const getErrorMessage = (existingErrorMsg) => {
    // Prevent error message from changing inside the error banner once the
    // computer comes back online.
    if (existingErrorMsg) return existingErrorMsg;

    return navigator.onLine
        ? 'We’re having trouble loading this chart…'
        : 'Your computer seems to be offline.';
};

// @ts-expect-error
function mapDataToBuckets(reports) {
    if (!reports.length || !Array.isArray(reports[0].data)) return;

    // @ts-expect-error
    return reports[0].data.map((item) => {
        if (isPercentBasedBucket(item)) {
            return createPercentBasedBucket(item);
        }

        const {bucket, count, total, sourceTypeLabel} = item;
        const fill = getColorForItem(item);

        return {
            name: bucket,
            y1: total && total.value,
            y2: count.value,
            y1_payload: total,
            y2_payload: count,
            fill: fill,
            sourceTypeLabel: sourceTypeLabel || undefined,
        };
    });
}

// @ts-expect-error
function isPercentBasedBucket(item) {
    return (
        item.bucketType === 'channels' &&
        item.segments &&
        item.segments.length > 0 &&
        item.segments[0].bucket === 'Won'
    );
}

// @ts-expect-error
function createPercentBasedBucket(item) {
    const {count} = item.segments[0];

    return {
        name: item.bucket,
        y1:
            count && count.subvalues && count.subvalues[0] && count.subvalues[0].percent
                ? count.subvalues[0].percent * 100
                : 0,
        y2: count && count.subvalues && count.subvalues[0] ? count.subvalues[0].value : 0,
        y1_payload: count && count.subvalues && count.subvalues[0] ? count.subvalues[0] : count,
        y2_payload:
            count && count.subvalues && count.subvalues[0] && count.subvalues
                ? count.subvalues[0]
                : count,
        fill: colors.green,
        isPercent: true,
    };
}

function jsonToTotalsReportData(result: ReportsResponse): ODRReportData | null | undefined {
    const chartData = mapDataToBuckets(result.reports);
    const exportLink = getExportLinkFromResult(result);

    if (chartData) {
        return {
            chartData,
            exportLink,
        };
    }
}

/**
 * Determines what color should be displayed for a particular bucket of data that we received from the API.
 * @param {object} item The item returned from API.
 * @param {string} item.bucketType The type of object represented in this bucket, i.e. 'outcomes'
 * @param {string} item.bucketSubtype The subtype represented in this bucket; for outcomes, 'won', 'lost', etc.
 * @return {string} A hexcode representing the color that should be used for this item.
 */
// @ts-expect-error
function getColorForItem(item) {
    const {bucketType, bucketSubtype} = item;

    if (bucketType === 'outcomes') {
        switch (bucketSubtype) {
            case 'won':
                return colors.green;
            case 'lost':
                return colors.rose;
            case 'cancelled':
                return colors.greyLt;
            default:
                return DEFAULT_BAR_COLOR;
        }
    }

    return DEFAULT_BAR_COLOR;
}

function transformDataForReport(result: ReportsResponse) {
    if (!result) return {};

    const reportData = jsonToTotalsReportData(result);

    if (!reportData) return {};

    return {result, reportData};
}

function addExportLink(exportLink: string) {
    // @ts-expect-error
    return ({result, reportData}) => {
        return {result, reportData: {...reportData, exportLink}};
    };
}

function notifyParent(onReportDataFetched?: ((...args: any) => any) | null) {
    // @ts-expect-error
    return ({result, reportData}) => {
        if (typeof onReportDataFetched === 'function') {
            onReportDataFetched({
                result,
                reportData,
            });
        }

        return {result, reportData};
    };
}

// -------------------------
// SetState functions
// -------------------------

function applyCachedData(cachedData: ODRReportData) {
    return () => ({
        reportData: cachedData,
    });
}

function stopLoading() {
    return {
        isLoading: false,
        isRefreshing: false,
    };
}

// @ts-expect-error
function startLoading(state) {
    return {
        ...state,
        isLoading: !state.reportData ? true : state.isLoading,
        isRefreshing: state.reportData ? true : state.isRefreshing,
    };
}

function clearError() {
    return {
        isErrored: false,
    };
}

function closingError() {
    return {
        errorIsClosing: true,
    };
}

function stopClosingError() {
    return {
        errorIsClosing: false,
    };
}

function failFetch() {
    return {
        reportData: undefined,
        isErrored: true,
        isLoading: false,
        errorIsClosing: false,
    };
}

function setData(reportDataForType: ODRReportData) {
    return () => ({
        reportData: reportDataForType,
    });
}
